import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { DataTable, StatusBadge, Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui';
import type { Column } from '@/components/ui';
import type { Invoice } from '@/types/api';
import { ArrowLeft, Download, Mail, Edit2 } from 'lucide-react';

export default function InvoicesPage() {
  const [showEmailModal, setShowEmailModal] = useState(false);

  const { data: invoicesData, isLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => apiClient.getInvoices(0, 20),
  });

  const { data: billingDetails } = useQuery({
    queryKey: ['billingDetails'],
    queryFn: () => apiClient.getBillingDetails(),
  });

  const handleDownload = async (invoice: Invoice) => {
    try {
      const blob = await apiClient.downloadInvoice(invoice.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download invoice:', error);
    }
  };

  const columns: Column<Invoice>[] = [
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      render: (invoice) => new Date(invoice.date).toLocaleDateString(),
    },
    {
      key: 'invoiceNumber',
      header: 'Invoice #',
      sortable: true,
    },
    {
      key: 'totalAmount',
      header: 'Amount',
      sortable: true,
      render: (invoice) => (
        <div>
          <span className="font-medium">
            £{invoice.totalAmount.toFixed(2)}
          </span>
          <span className="text-xs text-gray-500 block">
            (inc VAT)
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (invoice) => {
        const statusMap = {
          DRAFT: { type: 'pending' as const, label: 'Draft' },
          PENDING: { type: 'warning' as const, label: 'Pending' },
          PAID: { type: 'success' as const, label: 'Paid' },
          OVERDUE: { type: 'error' as const, label: 'Overdue' },
          VOID: { type: 'offline' as const, label: 'Void' },
        };
        const config = statusMap[invoice.status];
        return <StatusBadge status={config.type} label={config.label} size="sm" />;
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (invoice) => (
        <button
          onClick={() => handleDownload(invoice)}
          className="btn btn-outline h-8 px-3 text-sm"
        >
          <Download className="h-4 w-4 mr-1" />
          PDF
        </button>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/billing" className="text-gray-500 hover:text-gray-700">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and download your invoice history
          </p>
        </div>
      </div>

      <div className="card">
        <DataTable<Invoice>
          data={invoicesData?.content || []}
          columns={columns}
          keyExtractor={(invoice) => String(invoice.id)}
          pageSize={20}
          emptyMessage="No invoices found"
        />
      </div>

      {/* Invoice Email Settings */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-600">
                Invoice emails sent to:{' '}
                <span className="font-medium text-gray-900">
                  {billingDetails?.invoiceEmail || 'Not set'}
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowEmailModal(true)}
            className="btn btn-outline h-8 px-3 text-sm"
          >
            <Edit2 className="h-3 w-3 mr-1" />
            Change Email
          </button>
        </div>
      </div>

      {showEmailModal && billingDetails && (
        <ChangeEmailModal
          currentEmail={billingDetails.invoiceEmail}
          onClose={() => setShowEmailModal(false)}
        />
      )}
    </div>
  );
}

interface ChangeEmailModalProps {
  currentEmail: string;
  onClose: () => void;
}

function ChangeEmailModal({ currentEmail, onClose }: ChangeEmailModalProps) {
  const [email, setEmail] = useState(currentEmail);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Would call updateBillingDetails API here
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} size="sm">
      <ModalHeader onClose={onClose}>Change Invoice Email</ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <div>
            <label htmlFor="invoice-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              id="invoice-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input w-full"
              placeholder="accounts@example.com"
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <button type="button" onClick={onClose} className="btn btn-outline h-10">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary h-10">
            Save
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
